import { isMemoryArchiveType } from "@/lib/leaderMemoryVisibility";
export type BehavioralConfidence = "low" | "medium" | "high";

export type BehavioralObservationSource = "bi_v2" | "compatibility_only_v1" | "no_behavioral_data";

export type BehavioralSourceMode = "canonical_bi_v2" | "compatibility_only_v1" | "no_behavioral_data";

export type BehavioralEvidenceOrigin = "context_graph" | "compatibility_only_v1";

export type BehavioralPrimaryObservation = {
  behaviorKey: string;
  label: string;
  source: BehavioralObservationSource;
};

export type BehavioralContradiction = {
  behaviorKey: string;
  reason: string | null;
  source: "bi_v2";
  evidenceRefId: string | null;
};

export type BehavioralEvidenceRef = {
  id: string;
  label: string;
  sourceType: string;
  sourceId: string;
  createdAt: string | null;
  route: string | null;
  origin: BehavioralEvidenceOrigin;
};

export type BehavioralSourceSummary = {
  mode: BehavioralSourceMode;
  usedCompatibilityFallback: boolean;
  thinEvidence: boolean;
  canonicalThemeCount: number;
  graphEvidenceCount: number;
};

export type BehavioralSupportSummary =
  | {
      kind: "canonical";
      artifactCount: number;
      meetingCount: number;
      thin: boolean;
    }
  | {
      kind: "compatibility_only_v1";
      legacyMeetingCount: number;
      thin: boolean;
    }
  | {
      kind: "no_behavioral_data";
    };

export type BehavioralReadModel = {
  primaryObservation: BehavioralPrimaryObservation;
  confidence: BehavioralConfidence;
  contradictions: BehavioralContradiction[];
  evidenceRefs: BehavioralEvidenceRef[];
  sourceSummary: BehavioralSourceSummary;
  supportSummary: BehavioralSupportSummary;
};

export type BehavioralGraphNodeLike = {
  node_type?: string;
  source_type: string;
  source_id: string;
  title: string | null;
  created_at: string | null;
  metadata?: Record<string, unknown> | null;
};

export type BehavioralGraphState = {
  canonicalThemes: string[];
  contradictionCount: number;
  contradictionSignals: string[];
  latestEvidenceAt: string | null;
  recency: "fresh" | "recent" | "stale" | "unknown";
  thin: boolean;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function parseTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

export function normalizeBehavioralKey(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

export function toBehavioralLabel(value: string): string {
  return value
    .trim()
    .split(/[_-]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function normalizeBehavioralConfidence(
  value: unknown,
  fallback: BehavioralConfidence = "low",
): BehavioralConfidence {
  if (value === "high" || value === "medium" || value === "low") return value;
  return fallback;
}

export function downgradeBehavioralConfidence(value: BehavioralConfidence): BehavioralConfidence {
  if (value === "high") return "medium";
  if (value === "medium") return "low";
  return "low";
}

export function compactBehavioralStrings(items: Array<string | null | undefined>, limit = 6): string[] {
  const seen = new Set<string>();
  return items
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter((item) => item.length > 0)
    .filter((item) => {
      if (seen.has(item)) return false;
      seen.add(item);
      return true;
    })
    .slice(0, limit);
}

export function truncateBehavioralEvidenceText(value: string, maxLength = 180): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;
  if (maxLength <= 3) return normalized.slice(0, maxLength);
  return `${normalized.slice(0, maxLength - 3).trimEnd()}...`;
}

export function isThinBehavioralEvidence(params: {
  supportingArtifactCount: number;
  evidenceRefCount: number;
}): boolean {
  return params.supportingArtifactCount <= 1 || params.evidenceRefCount <= 1;
}

export function isBehavioralReadModelThin(params: {
  supportSummary: BehavioralSupportSummary;
  sourceSummary: BehavioralSourceSummary;
}): boolean {
  if (params.sourceSummary.thinEvidence) return true;
  if (params.supportSummary.kind === "canonical") return params.supportSummary.thin;
  if (params.supportSummary.kind === "compatibility_only_v1") return params.supportSummary.thin;
  return true;
}

function deriveRecency(latestEvidenceAt: string | null, referenceNowMs: number): BehavioralGraphState["recency"] {
  if (!latestEvidenceAt) return "unknown";

  const latestTs = new Date(latestEvidenceAt).getTime();
  if (Number.isNaN(latestTs)) return "unknown";

  const ageDays = (referenceNowMs - latestTs) / (1000 * 60 * 60 * 24);
  if (ageDays <= 14) return "fresh";
  if (ageDays <= 45) return "recent";
  return "stale";
}

function extractCanonicalTheme(node: BehavioralGraphNodeLike): string | null {
  if (
    node.source_type !== "behavioral_intelligence_v2_theme" &&
    node.source_type !== "behavioral_intelligence_v2_cluster"
  ) {
    return null;
  }

  const metadata = asRecord(node.metadata);
  const raw = typeof metadata?.behavior_theme === "string"
    ? metadata.behavior_theme
    : typeof metadata?.theme_name === "string"
      ? metadata.theme_name
      : node.title;

  if (typeof raw !== "string" || raw.trim().length === 0) return null;
  return toBehavioralLabel(raw);
}

function extractContradictionSignal(node: BehavioralGraphNodeLike): string | null {
  const metadata = asRecord(node.metadata);
  const raw = typeof metadata?.contradiction_signal === "string"
    ? metadata.contradiction_signal
    : typeof metadata?.contradiction_reason === "string"
      ? metadata.contradiction_reason
      : typeof metadata?.contradiction_summary === "string"
        ? metadata.contradiction_summary
        : null;

  return raw?.trim().length ? raw.trim() : null;
}

function extractContradictionCount(node: BehavioralGraphNodeLike): number {
  const metadata = asRecord(node.metadata);
  const raw = metadata?.contradiction_count;
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) return raw;
  if (typeof raw === "string") {
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return 0;
}

function extractWeightedSignalScore(node: BehavioralGraphNodeLike): number {
  const metadata = asRecord(node.metadata);
  const raw = metadata?.weighted_signal_score ?? metadata?.weightedSignalScore;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

export function buildGraphBehavioralState(
  nodes: BehavioralGraphNodeLike[],
  options: { referenceNowMs?: number } = {},
): BehavioralGraphState {
  nodes = nodes.filter(node => !isMemoryArchiveType(node.node_type) && !isMemoryArchiveType(node.source_type));
  const themeMap = new Map<string, { label: string; weightedSignalScore: number; firstSeenIndex: number }>();
  const contradictionSignalMap = new Map<string, {
    signal: string;
    contradictionCount: number;
    latestCreatedAtMs: number | null;
    firstSeenIndex: number;
  }>();
  let contradictionCount = 0;
  let latestEvidenceAt: string | null = null;
  let latestEvidenceAtMs: number | null = null;

  for (const [index, node] of nodes.entries()) {
    const theme = extractCanonicalTheme(node);
    if (theme) {
      const key = normalizeBehavioralKey(theme);
      const nextScore = extractWeightedSignalScore(node);
      const existing = themeMap.get(key);
      if (!existing) {
        themeMap.set(key, {
          label: theme,
          weightedSignalScore: nextScore,
          firstSeenIndex: index,
        });
      } else if (nextScore > existing.weightedSignalScore) {
        themeMap.set(key, {
          ...existing,
          label: theme,
          weightedSignalScore: nextScore,
        });
      }
    }

    const contradictionSignal = extractContradictionSignal(node);
    const nodeContradictionCount = extractContradictionCount(node);
    const nodeCreatedAtMs = parseTimestamp(node.created_at);
    if (contradictionSignal) {
      const existing = contradictionSignalMap.get(contradictionSignal);
      if (!existing) {
        contradictionSignalMap.set(contradictionSignal, {
          signal: contradictionSignal,
          contradictionCount: nodeContradictionCount,
          latestCreatedAtMs: nodeCreatedAtMs,
          firstSeenIndex: index,
        });
      } else {
        contradictionSignalMap.set(contradictionSignal, {
          signal: contradictionSignal,
          contradictionCount: Math.max(existing.contradictionCount, nodeContradictionCount),
          latestCreatedAtMs: existing.latestCreatedAtMs === null
            ? nodeCreatedAtMs
            : nodeCreatedAtMs === null
              ? existing.latestCreatedAtMs
              : Math.max(existing.latestCreatedAtMs, nodeCreatedAtMs),
          firstSeenIndex: existing.firstSeenIndex,
        });
      }
    }

    contradictionCount += nodeContradictionCount;

    if (nodeCreatedAtMs !== null && (latestEvidenceAtMs === null || nodeCreatedAtMs > latestEvidenceAtMs)) {
      latestEvidenceAt = node.created_at;
      latestEvidenceAtMs = nodeCreatedAtMs;
    }
  }

  contradictionCount = Math.max(contradictionCount, contradictionSignalMap.size);

  const referenceNowMs = options.referenceNowMs ?? Date.now();
  const canonicalThemes = [...themeMap.values()]
    .sort((a, b) => {
      const scoreDelta = b.weightedSignalScore - a.weightedSignalScore;
      if (scoreDelta !== 0) return scoreDelta;
      return a.firstSeenIndex - b.firstSeenIndex;
    })
    .map((entry) => entry.label)
    .slice(0, 3);
  const contradictionSignals = [...contradictionSignalMap.values()]
    .sort((a, b) => {
      const leftTime = a.latestCreatedAtMs ?? Number.NEGATIVE_INFINITY;
      const rightTime = b.latestCreatedAtMs ?? Number.NEGATIVE_INFINITY;
      const recencyDelta = rightTime - leftTime;
      if (recencyDelta !== 0) return recencyDelta;

      const countDelta = b.contradictionCount - a.contradictionCount;
      if (countDelta !== 0) return countDelta;

      return a.firstSeenIndex - b.firstSeenIndex;
    })
    .map((entry) => entry.signal)
    .slice(0, 2);

  return {
    canonicalThemes,
    contradictionCount,
    contradictionSignals,
    latestEvidenceAt,
    recency: deriveRecency(latestEvidenceAt, referenceNowMs),
    thin: themeMap.size === 0 || nodes.length <= 1,
  };
}
